import { useEffect, useState } from 'react';
import type { Contact, Occasion, OccasionType, CardDesignSnapshot } from '../../../../shared/src/types/database';
import CardDesignPreview, { type DesignData } from '../cards/CardDesignPreview';
import CardDesignPicker from '../cards/CardDesignPicker';
import CardInsidePreview from '../cards/CardInsidePreview';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { deriveGreeting } from '../../lib/greeting';

const CHARS_PER_PRINT_LINE = 45;
const MAX_PRINT_LINES      = 14;
const MAX_CHARS            = 650;

function countPrintLines(text: string): number {
  if (!text) return 0;
  return text.split('\n').reduce((total, line) =>
    total + (line.length === 0 ? 1 : Math.ceil(line.length / CHARS_PER_PRINT_LINE)), 0);
}

type ContactWithOccasions = Contact & { occasions: Occasion[] };

interface ComingUpSectionProps {
  contacts:            ContactWithOccasions[];
  onOccasionUpdated?:  (o: Occasion) => void;
  onOccasionDeleted?:  (id: string) => void;
}

// ─── Occasion display config ──────────────────────────────────────────────────

const OCCASION_META: Record<OccasionType, { label: string; emoji: string }> = {
  birthday:        { label: 'Birthday',        emoji: '🎂' },
  anniversary:     { label: 'Anniversary',     emoji: '💍' },
  christmas:       { label: 'Christmas',       emoji: '🎄' },
  valentines:      { label: "Valentine's Day", emoji: '💝' },
  mothers_day:     { label: "Mother's Day",    emoji: '🌸' },
  fathers_day:     { label: "Father's Day",    emoji: '👔' },
  graduation:      { label: 'Graduation',      emoji: '🎓' },
  thinking_of_you: { label: 'Thinking of You', emoji: '💌' },
  custom:          { label: 'Custom',          emoji: '✨' },
};

// ─── Days-until helpers ───────────────────────────────────────────────────────

function daysUntil(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + 'T00:00:00');
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

function daysLabel(days: number): string {
  if (days < 0)  return 'Passed';
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days <= 7)  return `${days} days`;
  if (days <= 30) return `${days} days`;
  return `${days} days`;
}

function urgencyColor(days: number): string {
  if (days <= 0)  return 'text-gray-400';
  if (days <= 3)  return 'text-rose-600';
  if (days <= 14) return 'text-gold-dark';
  return 'text-sage';
}

// ─── Milestone helper ─────────────────────────────────────────────────────────

const MILESTONE_AGES = [30, 40, 50, 60, 70, 80, 90, 100];

function getMilestoneAge(occasion: Occasion): number | null {
  if (occasion.occasion_type !== 'birthday' || !occasion.birth_year || !occasion.next_send_date) {
    return null;
  }
  const sendYear = parseInt(occasion.next_send_date.split('-')[0], 10);
  const age = sendYear - occasion.birth_year;
  return MILESTONE_AGES.includes(age) ? age : null;
}

// ─── History modal ────────────────────────────────────────────────────────────

interface HistoryEntry {
  id: string;
  sent_at: string | null;
  scheduled_send_date: string;
  card_design_snapshot: CardDesignSnapshot | null;
}

function OccasionHistoryModal({
  occasionId,
  contactName,
  occasionLabel,
  onClose,
}: {
  occasionId:    string;
  contactName:   string;
  occasionLabel: string;
  onClose:       () => void;
}) {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ history: HistoryEntry[] }>(`/api/occasions/${occasionId}/history`)
      .then((json) => setHistory(json.history ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [occasionId]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(26,20,16,0.35)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-[24px] shadow-warm-lg w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <p className="font-body text-xs text-sage font-semibold uppercase tracking-widest mb-0.5">
              Card history
            </p>
            <h3 className="font-heading text-lg font-semibold text-navy leading-snug">
              {occasionLabel} · {contactName}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="mt-0.5 rounded-full p-1.5 text-gray-400 hover:text-navy hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-4">
          {loading && (
            <p className="font-body text-sm text-gray-400 text-center py-8">Loading…</p>
          )}
          {!loading && history.length === 0 && (
            <div className="text-center py-8">
              <div className="text-3xl mb-3">📭</div>
              <p className="font-body text-sm text-gray-500">No cards sent yet for this occasion.</p>
            </div>
          )}
          {!loading && history.length > 0 && (
            <div className="flex flex-col gap-4">
              {history.map((entry) => {
                const sentDate = entry.sent_at
                  ? new Date(entry.sent_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                  : new Date(entry.scheduled_send_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                const message = entry.card_design_snapshot?.message;
                const designName = entry.card_design_snapshot?.design_name;
                return (
                  <div key={entry.id} className="rounded-[14px] bg-gray-50 p-4">
                    <p className="font-body text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Sent {sentDate}{designName ? ` · ${designName}` : ''}
                    </p>
                    {message ? (
                      <p className="font-body text-sm text-gray-700 leading-relaxed whitespace-pre-line">
                        {message}
                      </p>
                    ) : (
                      <p className="font-body text-sm text-gray-400 italic">No message recorded.</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Occasion detail modal ────────────────────────────────────────────────────

const OCCASION_LABEL_FULL: Record<OccasionType, string> = {
  birthday:        'Birthday',
  anniversary:     'Anniversary',
  christmas:       'Christmas',
  valentines:      "Valentine's Day",
  mothers_day:     "Mother's Day",
  fathers_day:     "Father's Day",
  graduation:      'Graduation',
  thinking_of_you: 'Thinking of You',
  custom:          'Custom',
};

function OccasionDetailModal({
  occasion,
  contact,
  onClose,
  onOccasionUpdated,
  onOccasionDeleted,
}: {
  occasion:            Occasion;
  contact:             Contact;
  onClose:             () => void;
  onOccasionUpdated?:  (o: Occasion) => void;
  onOccasionDeleted?:  (id: string) => void;
}) {
  const { profile }  = useAuth();
  const meta         = OCCASION_META[occasion.occasion_type];
  const label        = occasion.occasion_type === 'custom' && occasion.custom_occasion_name
    ? occasion.custom_occasion_name
    : OCCASION_LABEL_FULL[occasion.occasion_type];
  const [selectedDesignId, setSelectedDesignId] = useState<string | null>(occasion.card_design_id ?? null);
  const [greeting,         setGreeting        ] = useState(
    occasion.card_greeting ??
    deriveGreeting(contact.relationship_type, occasion.occasion_type, contact.name)
  );
  const [message,          setMessage         ] = useState((occasion as Occasion & { current_message?: string }).current_message ?? '');
  const [saving,           setSaving          ] = useState(false);
  const [saved,            setSaved           ] = useState(false);
  const [confirmRemove,    setConfirmRemove   ] = useState(false);
  const [removing,         setRemoving        ] = useState(false);
  const [activeTab,        setActiveTab       ] = useState<'setup' | 'preview'>('setup');

  const [cardClosing,  setCardClosing ] = useState(profile?.card_closing   ?? 'With love,');
  const [cardFromLine, setCardFromLine] = useState(profile?.card_from_line ?? profile?.first_name ?? '');

  const dateStr = occasion.next_send_date
    ? new Date(occasion.next_send_date + 'T00:00:00').toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
      })
    : null;

  const days = occasion.next_send_date ? daysUntil(occasion.next_send_date) : null;

  async function handleSave() {
    setSaving(true);
    try {
      const json = await apiFetch<{ occasion: Occasion }>(`/api/occasions/${occasion.id}`, {
        method: 'PUT',
        body:   JSON.stringify({
          card_design_id: selectedDesignId,
          card_greeting:  greeting,
          current_message: message,
        }),
      });
      if (cardClosing !== (profile?.card_closing ?? 'With love,') || cardFromLine !== (profile?.card_from_line ?? profile?.first_name ?? '')) {
        await apiFetch('/api/auth/profile', {
          method: 'PATCH',
          body:   JSON.stringify({ cardClosing, cardFromLine }),
        });
      }
      setSaved(true);
      onOccasionUpdated?.(json.occasion);
      setTimeout(onClose, 800);
    } catch {
      setSaving(false);
    }
  }

  async function handleRemove() {
    setRemoving(true);
    try {
      await apiFetch(`/api/occasions/${occasion.id}`, { method: 'DELETE' });
      onOccasionDeleted?.(occasion.id);
      onClose();
    } catch {
      setRemoving(false);
      setConfirmRemove(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: 'rgba(26,20,16,0.45)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white w-full sm:max-w-md rounded-t-[24px] sm:rounded-[24px]
                   shadow-warm-xl max-h-[90dvh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 pt-5 pb-4 border-b border-gray-100 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="text-xl">{meta.emoji}</span>
              <h3 className="font-heading text-lg font-semibold text-navy">{label}</h3>
            </div>
            <p className="font-body text-sm text-gray-500">for {contact.name}</p>
            {dateStr && (
              <p className="font-body text-xs text-sage mt-1">
                {days === 0 ? 'Today!' : days === 1 ? 'Tomorrow' : days !== null ? `In ${days} days` : ''} · {dateStr}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 rounded-full p-1.5 text-gray-400 hover:text-navy hover:bg-gray-100 transition-colors"
            aria-label="Close"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex-shrink-0 flex gap-1 px-6 pt-3 pb-0">
          {(['setup', 'preview'] as const).map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`font-body text-xs font-semibold px-4 py-1.5 rounded-full transition-all ${
                activeTab === tab
                  ? 'bg-navy text-white'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              {tab === 'setup' ? 'Set up card' : 'Preview'}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">

          {activeTab === 'setup' ? (
            <>
              {/* Card design picker */}
              <div>
                <p className="font-body text-sm font-semibold text-navy mb-1">
                  {selectedDesignId ? 'Card design' : 'Pick a card design'}
                </p>
                {!selectedDesignId && (
                  <p className="font-body text-xs text-gray-400 mb-3">
                    You're ahead of schedule, choose a design now and we'll use it when this occasion comes up.
                  </p>
                )}
                <CardDesignPicker
                  occasionType={occasion.occasion_type}
                  selected={selectedDesignId}
                  onChange={setSelectedDesignId}
                  cardGreeting={greeting}
                  cardClosing={cardClosing}
                  cardFromLine={cardFromLine}
                />
              </div>

              {/* Card greeting */}
              <div>
                <label className="font-body text-sm font-semibold text-navy block mb-1">
                  Opening greeting
                </label>
                <input
                  type="text"
                  value={greeting}
                  onChange={(e) => setGreeting(e.target.value)}
                  placeholder="Dear [Name],"
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5
                             font-body text-sm text-navy placeholder-gray-400
                             focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage/60
                             transition-colors"
                />
              </div>

              {/* Message */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="font-body text-sm font-semibold text-navy">
                    Your message
                  </label>
                  {(() => {
                    const lines   = countPrintLines(message);
                    const warn    = lines > 6 || message.length > 280;
                    const atLimit = lines >= MAX_PRINT_LINES || message.length >= MAX_CHARS;
                    return message ? (
                      <span className={`font-body text-[10px] ${atLimit ? 'text-red-500' : warn ? 'text-amber-600' : 'text-gray-400'}`}>
                        {lines}/{MAX_PRINT_LINES} lines · {message.length}/{MAX_CHARS}
                        {atLimit ? ', limit reached' : ''}
                      </span>
                    ) : null;
                  })()}
                </div>
                <textarea
                  value={message}
                  rows={5}
                  placeholder="Write your message here, or leave it blank and we'll prompt you closer to the date."
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val.length <= MAX_CHARS && countPrintLines(val) <= MAX_PRINT_LINES) setMessage(val);
                  }}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5
                             font-body text-sm text-navy placeholder-gray-400 resize-none
                             focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage/60
                             transition-colors"
                />
              </div>

              {/* Closing phrase */}
              <div>
                <label className="font-body text-sm font-semibold text-navy block mb-1">
                  Closing phrase
                </label>
                <input
                  type="text"
                  value={cardClosing}
                  onChange={(e) => setCardClosing(e.target.value)}
                  placeholder="With love,"
                  maxLength={60}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5
                             font-body text-sm text-navy placeholder-gray-400
                             focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage/60
                             transition-colors"
                />
                <div className="flex flex-wrap gap-2 mt-2">
                  {['With love,', 'Love,', 'Warmly,', 'Xoxo,', 'Thinking of you,', 'Sincerely,',
                    'Cheers,', 'Always,', 'Your friend,', 'Forever yours,'].map((chip) => (
                    <button
                      key={chip}
                      type="button"
                      onClick={() => setCardClosing(chip)}
                      className="font-body text-xs px-2.5 py-1 rounded-full border border-gray-200 text-gray-500 hover:border-sage/50 hover:text-sage transition-colors"
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              </div>

              {/* From line */}
              <div>
                <label className="font-body text-sm font-semibold text-navy block mb-1">
                  From line
                </label>
                <input
                  type="text"
                  value={cardFromLine}
                  onChange={(e) => setCardFromLine(e.target.value)}
                  placeholder={profile?.first_name ?? 'Your name'}
                  maxLength={80}
                  className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5
                             font-body text-sm text-navy placeholder-gray-400
                             focus:outline-none focus:ring-2 focus:ring-sage/40 focus:border-sage/60
                             transition-colors"
                />
                <p className="font-body text-xs text-gray-400 mt-1">Leave blank to use your first name.</p>
              </div>

              {/* Past cards */}
              {occasion.last_sent_date && (
                <p className="font-body text-xs text-gray-400">
                  Last card sent {new Date(occasion.last_sent_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}.
                </p>
              )}
            </>
          ) : (
            /* ── Preview tab ── */
            <div className="flex flex-col gap-3">
              <p className="font-body text-xs text-gray-400">
                This is how the inside of your card will look when it's printed.
              </p>
              <CardInsidePreview
                design={{ accent_color: '#E8C12E', bg_color: '#fff', bg_type: 'solid',
                          emoji: '', heading: '', id: '', name: '', pattern: 'none',
                          pattern_color: '', style: '', tagline: '', text_color: '#1A1410' }}
                message={message}
                cardGreeting={greeting}
                cardClosing={cardClosing}
                cardFromLine={cardFromLine}
                placeholder="Your message will appear here once you write it on the Set up card tab."
              />
              {!message && (
                <p className="font-body text-xs text-amber-600 text-center">
                  No message written yet, add one on the "Set up card" tab.
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {confirmRemove ? (
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100">
            <p className="font-body text-sm text-center text-gray-600 mb-3">
              Remove this occasion for {contact.name}?
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmRemove(false)}
                disabled={removing}
                className="btn-secondary flex-1 text-sm"
              >
                Keep it
              </button>
              <button
                type="button"
                onClick={handleRemove}
                disabled={removing}
                className="flex-1 text-sm rounded-xl bg-red-50 text-red-600 font-body font-semibold
                           hover:bg-red-100 transition-colors px-4 py-2.5 disabled:opacity-60"
              >
                {removing ? 'Removing…' : 'Yes, remove'}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100">
            <div className="flex gap-3">
              <button onClick={onClose} disabled={saving} className="btn-secondary flex-1 text-sm">
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || saved}
                className="btn-primary flex-1 text-sm disabled:opacity-60"
              >
                {saved ? '✓ Saved!' : saving ? 'Saving…' : 'Save'}
              </button>
            </div>
            {onOccasionDeleted && (
              <button
                type="button"
                onClick={() => setConfirmRemove(true)}
                className="w-full mt-2 font-body text-xs text-red-400 hover:text-red-600 transition-colors text-center py-1"
              >
                Remove this occasion
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Occasion card ────────────────────────────────────────────────────────────

function useDesign(designId: string | null): DesignData | null {
  const [design, setDesign] = useState<DesignData | null>(null);
  useEffect(() => {
    if (!designId) { setDesign(null); return; }
    apiFetch<{ design: DesignData }>(`/api/designs/${encodeURIComponent(designId)}`)
      .then((json) => setDesign(json?.design ?? null))
      .catch(() => {});
  }, [designId]);
  return design;
}

function OccasionCard({
  occasion,
  contact,
  onOccasionUpdated,
  onOccasionDeleted,
}: {
  occasion:            Occasion;
  contact:             Contact;
  onOccasionUpdated?:  (o: Occasion) => void;
  onOccasionDeleted?:  (id: string) => void;
}) {
  const { profile } = useAuth();
  const meta  = OCCASION_META[occasion.occasion_type];
  const days  = occasion.next_send_date ? daysUntil(occasion.next_send_date) : null;
  const label = occasion.occasion_type === 'custom' && occasion.custom_occasion_name
    ? occasion.custom_occasion_name
    : meta.label;

  const urgency      = days !== null ? urgencyColor(days) : 'text-gray-400';
  const design       = useDesign(occasion.card_design_id ?? null);
  const milestoneAge = getMilestoneAge(occasion);

  const [showDetail,  setShowDetail ] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const dateDisplay = occasion.next_send_date
    ? new Date(occasion.next_send_date + 'T00:00:00').toLocaleDateString('en-US', {
        month: 'short',
        day:   'numeric',
      })
    : ',';

  return (
    <>
      <div
        className="flex-shrink-0 w-48 rounded-[20px] bg-white shadow-warm p-5 flex flex-col gap-3
                   cursor-pointer hover:shadow-warm-lg transition-shadow"
        onClick={() => setShowDetail(true)}
      >
        {/* Design thumbnail or emoji */}
        <div className="flex items-start justify-between gap-2">
          {design ? (
            <CardDesignPreview design={design} size="xs" showName={false} className="self-start" />
          ) : (
            <div className="flex items-center gap-2">
              <div className="text-3xl leading-none">{meta.emoji}</div>
              {/* No-design hint */}
              <span className="font-body text-[9px] text-gray-300 leading-tight">tap to<br/>pick card</span>
            </div>
          )}

          {/* Milestone badge */}
          {milestoneAge && (
            <span
              className="flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-body font-bold"
              style={{ background: '#FEF3C7', color: '#92400E' }}
            >
              🎉 {milestoneAge}
            </span>
          )}
        </div>

        {/* Days countdown */}
        {days !== null && (
          <div className={`font-heading text-2xl font-bold leading-none ${urgency}`}
            style={days <= 3 && days > 0 ? { color: '#c0534a' } : days <= 14 && days > 0 ? { color: '#9A7B2C' } : {}}
          >
            {daysLabel(days)}
          </div>
        )}

        {/* Occasion name */}
        <div>
          <p className="font-body text-sm font-semibold text-navy leading-snug">{label}</p>
          <p className="font-body text-xs text-gray-400 mt-0.5">{contact.name}</p>
        </div>

        {/* Date + history link */}
        <div className="flex items-center justify-between mt-auto pt-1" onClick={(e) => e.stopPropagation()}>
          <p className="font-body text-xs text-gray-300">{dateDisplay}</p>
          {occasion.last_sent_date && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowHistory(true); }}
              className="font-body text-[10px] text-sage hover:text-navy transition-colors underline underline-offset-2"
            >
              Past cards
            </button>
          )}
        </div>
      </div>

      {showDetail && (
        <OccasionDetailModal
          occasion={occasion}
          contact={contact}
          onClose={() => setShowDetail(false)}
          onOccasionUpdated={onOccasionUpdated}
          onOccasionDeleted={onOccasionDeleted}
        />
      )}

      {showHistory && (
        <OccasionHistoryModal
          occasionId={occasion.id}
          contactName={contact.name}
          occasionLabel={label}
          onClose={() => setShowHistory(false)}
        />
      )}
    </>
  );
}

// ─── ComingUpSection ──────────────────────────────────────────────────────────

export default function ComingUpSection({ contacts, onOccasionUpdated, onOccasionDeleted }: ComingUpSectionProps) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const cutoff = new Date(today);
  cutoff.setDate(cutoff.getDate() + 90);   // show next 90 days

  // Flatten contacts → occasions, attach contact, filter + sort
  type OccasionWithContact = Occasion & { contact: Contact };

  const upcoming: OccasionWithContact[] = contacts
    .flatMap((c) =>
      c.occasions.map((o) => ({ ...o, contact: c as Contact })),
    )
    .filter((o) => {
      if (!o.next_send_date) return false;
      const d = new Date(o.next_send_date + 'T00:00:00');
      return d >= today && d <= cutoff;
    })
    .sort((a, b) =>
      (a.next_send_date ?? '').localeCompare(b.next_send_date ?? ''),
    );

  if (upcoming.length === 0) {
    return (
      <section className="mb-10">
        <h2 className="font-heading text-xl font-semibold text-navy mb-4">Coming Up</h2>
        <div className="rounded-[20px] bg-white shadow-warm px-8 py-10 text-center">
          <div className="text-4xl mb-3">📭</div>
          <p className="font-body text-sm text-gray-500">
            Nothing in the next 90 days.
            <br />
            Add occasions to your contacts to see them here.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-10">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-heading text-xl font-semibold text-navy">
          Coming Up
        </h2>
        <span className="font-body text-xs text-gray-400">
          Next 90 days · {upcoming.length} occasion{upcoming.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Horizontal scroll on all widths; wraps on large screens */}
      <div className="flex gap-4 overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap">
        {upcoming.map((o) => (
          <OccasionCard
            key={o.id}
            occasion={o}
            contact={o.contact}
            onOccasionUpdated={onOccasionUpdated}
            onOccasionDeleted={onOccasionDeleted}
          />
        ))}
      </div>
    </section>
  );
}
